// src/components/auth/LoginChooser.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Auth } from "aws-amplify";
import { useHistory, useLocation } from "react-router-dom";
import "../../auth.css";

const AUTH_STATUS_BASE = `https://${process.env.REACT_APP_ACCOUNT_API_URL}`;
const LS_KEY = "ph_prefill_email";

export default function LoginChooser() {
  const history = useHistory();
  const location = useLocation();

  // Stages: EMAIL -> PASSWORD -> (optional) NEW_PASSWORD
  const [stage, setStage] = useState("EMAIL");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // NEW_PASSWORD_REQUIRED inline
  const [newPw1, setNewPw1] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [userForNewPw, setUserForNewPw] = useState(null);

  // show/hide toggles for NEW_PASSWORD fields
  const [showNewPw1, setShowNewPw1] = useState(false);
  const [showNewPw2, setShowNewPw2] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const passwordRef = useRef(null);

  // Prefill email from ?email= or localStorage
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const fromQS = (sp.get("email") || "").trim();
    const fromLS =
      (!fromQS && typeof localStorage !== "undefined" && localStorage.getItem(LS_KEY)) || "";
    setEmail(fromQS || fromLS || "");
  }, [location.search]);

  // Focus password when we move to that stage
  useEffect(() => {
    if (stage === "PASSWORD" && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [stage]);

  function rememberEmail(v) {
    try { localStorage.setItem(LS_KEY, v || ""); } catch {}
  }

  function goForgotCustom() {
    rememberEmail(email);
    const qs = email ? `?email=${encodeURIComponent(email)}` : "";
    history.push(`/auth/forgot${qs}`);
  }

  async function sendCodeAndGo() {
    try {
      await Auth.forgotPassword(email.trim());
    } catch {
      // Avoid user enumeration; still proceed to code screen.
    }
    history.replace(`/auth/reset?email=${encodeURIComponent(email)}`, { sent: true });
  }

  // Step 1: after email, decide reset vs login
  async function continueOrReset() {
    if (!email) return;
    setBusy(true);
    setError("");
    rememberEmail(email);

    try {
      const r = await fetch(
        `${AUTH_STATUS_BASE}/auth/status?username=${encodeURIComponent(email)}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );
      const data = await r.json().catch(() => ({}));
      const reset = !!data.reset;

      if (reset) {
        await sendCodeAndGo();
      } else {
        setStage("PASSWORD");
      }
    } catch {
      setStage("PASSWORD");
    } finally {
      setBusy(false);
    }
  }

  // Step 2: native sign-in with Amplify
  async function signInNative() {
    if (!email || !password) return;
    setBusy(true);
    setError("");
    rememberEmail(email);

    try {
      const user = await Auth.signIn(email.trim(), password);

      if (user?.challengeName === "NEW_PASSWORD_REQUIRED") {
        setUserForNewPw(user);
        setStage("NEW_PASSWORD");
        return;
      }

      window.location.replace("/");
    } catch (e) {
      if (e?.challengeName === "NEW_PASSWORD_REQUIRED" ||
          /NEW_PASSWORD_REQUIRED/i.test(e?.message || "")) {
        setUserForNewPw(e);
        setStage("NEW_PASSWORD");
        setBusy(false);
        return;
      }

      const code = e?.code || e?.name || "";
      const msg = (e?.message || "").toLowerCase();

      if (code === "PasswordResetRequiredException" || msg.includes("password reset required")) {
        await sendCodeAndGo();
      } else if (code === "InvalidPasswordException" && msg.includes("temporary")) {
        await sendCodeAndGo();
      } else if (code === "UserNotConfirmedException") {
        setError("Your email isn’t confirmed yet. Please check your inbox for a verification link.");
      } else if (code === "NotAuthorizedException" || code === "UserNotFoundException") {
        setError("Invalid email or password.");
      } else {
        setError(e?.message || "We couldn’t sign you in. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  // Step 3: complete NEW_PASSWORD_REQUIRED (no email code)
  const req = useMemo(() => {
    const length = newPw1.length >= 12;
    const upper  = /[A-Z]/.test(newPw1);
    const lower  = /[a-z]/.test(newPw1);
    const number = /\d/.test(newPw1);
    const symbol = /[^A-Za-z0-9]/.test(newPw1);
    return { length, upper, lower, number, symbol };
  }, [newPw1]);
  const allOk   = req.length && req.upper && req.lower && req.number && req.symbol;
  const matchOk = newPw1.length > 0 && newPw1 === newPw2;

  async function completeNewPassword() {
    if (!userForNewPw) {
      setError("Session expired. Please sign in again with your temporary password.");
      setStage("PASSWORD");
      return;
    }
    if (!allOk) { setError("Your password does not meet the requirements."); return; }
    if (!matchOk) { setError("Passwords must match."); return; }

    setBusy(true);
    setError("");
    try {
      await Auth.completeNewPassword(userForNewPw, newPw1, {});
      window.location.replace("/");
    } catch (e) {
      setError(e?.message || "Could not set your new password. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    if (stage === "EMAIL") continueOrReset();
    else if (stage === "PASSWORD") signInNative();
    else if (stage === "NEW_PASSWORD") completeNewPassword();
  }

  const ReqItem = ({ ok, children }) => (
    <li className={`ph-req ${ok ? "ok" : "bad"}`}>
      <span aria-hidden="true" style={{ display: "inline-block", width: 18 }}>
        {ok ? "✓" : "✕"}
      </span>
      <span>{children}</span>
    </li>
  );

  return (
    <div className="ph-auth-wrap">
      <div className="ph-auth-card">
        <h1 className="ph-auth-title">
          {stage === "NEW_PASSWORD" ? "Set a new password" : "Sign in to your account"}
        </h1>

        <form onSubmit={handleSubmit}>
          {/* Email step */}
          <label className="ph-label" htmlFor="ph-email">Email Address</label>
          <input
            id="ph-email"
            className="ph-field"
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            disabled={busy || stage !== "EMAIL"}
          />

          {/* Password step */}
          {stage === "PASSWORD" && (
            <>
              <label className="ph-label" htmlFor="ph-password">Password</label>
              <input
                id="ph-password"
                className="ph-field"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                ref={passwordRef}
                disabled={busy}
              />
            </>
          )}

          {/* NEW_PASSWORD_REQUIRED with show/hide toggles */}
          {stage === "NEW_PASSWORD" && (
            <>
              <label className="ph-label" htmlFor="ph-new1">New Password</label>
              <div className="ph-field-wrap">
                <input
                  id="ph-new1"
                  className={`ph-field ${newPw1 && !allOk ? "invalid" : ""}`}
                  type={showNewPw1 ? "text" : "password"}
                  value={newPw1}
                  onChange={(e) => setNewPw1(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Enter a new password"
                />
                <button
                  type="button"
                  className="ph-toggle"
                  onClick={() => setShowNewPw1((s) => !s)}
                  aria-label={showNewPw1 ? "Hide password" : "Show password"}
                  aria-pressed={showNewPw1}
                  disabled={busy}
                >
                  {showNewPw1 ? "Hide" : "Show"}
                </button>
              </div>

              <ul className="ph-req-list" aria-live="polite" aria-atomic="true" style={{ margin: "8px 0 12px" }}>
                <ReqItem ok={req.length}>At least 12 characters</ReqItem>
                <ReqItem ok={req.upper}>Contains an uppercase letter (A–Z)</ReqItem>
                <ReqItem ok={req.lower}>Contains a lowercase letter (a–z)</ReqItem>
                <ReqItem ok={req.number}>Contains a number (0–9)</ReqItem>
                <ReqItem ok={req.symbol}>Contains a symbol (e.g., !@#$%)</ReqItem>
              </ul>

              <label className="ph-label" htmlFor="ph-new2">Confirm New Password</label>
              <div className="ph-field-wrap">
                <input
                  id="ph-new2"
                  className="ph-field"
                  type={showNewPw2 ? "text" : "password"}
                  value={newPw2}
                  onChange={(e) => setNewPw2(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Re-enter new password"
                />
                <button
                  type="button"
                  className="ph-toggle"
                  onClick={() => setShowNewPw2((s) => !s)}
                  aria-label={showNewPw2 ? "Hide password" : "Show password"}
                  aria-pressed={showNewPw2}
                  disabled={busy}
                >
                  {showNewPw2 ? "Hide" : "Show"}
                </button>
              </div>

              <div className={`ph-req ${matchOk ? "ok" : "bad"}`} aria-live="polite" style={{ margin: "6px 0 12px" }}>
                <span aria-hidden="true" style={{ display: "inline-block", width: 18 }}>
                  {matchOk ? "✓" : "✕"}
                </span>
                Passwords match
              </div>
            </>
          )}

          {error && <div className="ph-error" role="alert">{error}</div>}

          <button
            className="ph-button"
            type="submit"
            disabled={
              busy ||
              !email ||
              (stage === "PASSWORD" && !password) ||
              (stage === "NEW_PASSWORD" && (!newPw1 || !newPw2))
            }
          >
            {busy
              ? stage === "PASSWORD"
                ? "Signing in…"
                : stage === "NEW_PASSWORD"
                  ? "Updating…"
                  : "Checking…"
              : stage === "PASSWORD"
                ? "Sign in"
                : stage === "NEW_PASSWORD"
                  ? "Set password"
                  : "Continue"}
          </button>
        </form>

        {stage !== "NEW_PASSWORD" && (
          <p className="ph-hint">
            <a
              className="ph-link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                goForgotCustom();
              }}
            >
              Reset your password
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
